import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { hikingPath } from '../utils/paths';
import PhotoGallery from './PhotoGallery';
import { formatScheduleDateTime } from '../utils/scheduleTimeZone';
import {
  formatDistance,
  formatDuration,
  formatElevation,
  formatOutingPace,
  formatTrailDifficulty,
  formatTrailRouteShape,
  formatTrailTerrain,
} from '../config/hikerProfileOptions';
import './PublicHikingOuting.css';

const PublicHikingOuting = () => {
  const { shareToken } = useParams();
  const [outing, setOuting] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await api.getPublicHikingOuting(shareToken);
        setOuting(data);
      } catch (err) {
        setError(err.message || 'Shared outing not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareToken]);

  if (loading) return <div className="loading">Loading shared outing...</div>;
  if (error || !outing) return <div className="error-message">{error || 'Not found'}</div>;

  return (
    <div className="public-hiking-outing">
      <p className="public-hiking-outing__eyebrow">{outing.clubName || 'Hiking club'}</p>
      <h1>{outing.title}</h1>
      <p className="public-hiking-outing__when">{formatScheduleDateTime(outing.outingDate)}</p>
      <p className="public-hiking-outing__meet">
        Meeting: <strong>{outing.meetingLocation}</strong>
      </p>
      {outing.meetingNotes && <p>{outing.meetingNotes}</p>}
      {outing.pace && <p>Pace: {formatOutingPace(outing.pace)}</p>}
      {(outing.resolvedDurationMinutes ?? outing.expectedDurationMinutes) != null && (
        <p>
          Planned time on trail:{' '}
          {formatDuration(outing.resolvedDurationMinutes ?? outing.expectedDurationMinutes)}
        </p>
      )}
      {outing.description && <p>{outing.description}</p>}
      {outing.status && outing.status !== 'SCHEDULED' && (
        <p className="public-hiking-outing__status">Status: {outing.status}</p>
      )}

      {outing.trail && (
        <section className="public-hiking-outing__section">
          <h2>Trail</h2>
          <h3>{outing.trail.title}</h3>
          {outing.trail.parkName && <p>{outing.trail.parkName}</p>}
          {outing.trail.distanceKm != null && <p>{formatDistance(outing.trail.distanceKm)}</p>}
          {outing.trail.elevationGainM != null && <p>{formatElevation(outing.trail.elevationGainM)}</p>}
          <p>
            {[
              outing.trail.difficulty && formatTrailDifficulty(outing.trail.difficulty),
              outing.trail.terrain && formatTrailTerrain(outing.trail.terrain),
              outing.trail.routeShape && formatTrailRouteShape(outing.trail.routeShape),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {outing.trail.trailheadLocation && <p>Trailhead: {outing.trail.trailheadLocation}</p>}
          {outing.trail.parkingNotes && <p>Parking: {outing.trail.parkingNotes}</p>}
          {outing.trail.mapUrl && (
            <a href={outing.trail.mapUrl} target="_blank" rel="noreferrer">
              Open map
            </a>
          )}
          <PhotoGallery photos={outing.trail.photoUrls || []} />
        </section>
      )}

      {(outing.packList || []).length > 0 && (
        <section className="public-hiking-outing__section">
          <h2>What to pack</h2>
          <ul className="public-hiking-outing__pack-list">
            {outing.packList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="public-hiking-outing__section">
        <h2>Who is hiking</h2>
        <div className="public-hiking-outing__counts">
          <span>Going {outing.availableCount}</span>
          <span>Maybe {outing.maybeCount}</span>
          <span>Reserve {outing.reserveCount}</span>
          <span>Out {outing.unavailableCount}</span>
          {outing.hikerCapacity != null && <span>Capacity {outing.hikerCapacity}</span>}
        </div>
        {outing.clubId && (
          <p>
            <Link to={hikingPath(`/outings/${outing.id}`)}>Open outing to vote</Link>
          </p>
        )}
      </section>

      {(outing.photoUrls || []).length > 0 && (
        <section className="public-hiking-outing__section">
          <h2>Photos</h2>
          <PhotoGallery photos={outing.photoUrls} />
        </section>
      )}
    </div>
  );
};

export default PublicHikingOuting;
